import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../Context/AuthContext';
import { useBook } from '../../Context/BookContext';
import PricingSelector from '../PricingSelector';
// no direct API calls here; handled via utils in onPurchase

const CreditsPage: React.FC = () => {
  const { user } = useAuth();
  const { availableImages, fetchImageCredits, selectedBook } = useBook();
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [addCreditsMessage, setAddCreditsMessage] = useState('');

  useEffect(() => {
    // Check for URL parameters to show purchase dialog
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for successful payment
    if (urlParams.get('success') === 'true') {
      setAddCreditsMessage('Payment successful! Your credits have been added.');
      fetchImageCredits(); // Refresh credits
      setTimeout(() => setAddCreditsMessage(''), 10000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check for cancelled payment
    if (urlParams.get('cancelled') === 'true') {
      setAddCreditsMessage('Payment was cancelled.');
      setTimeout(() => setAddCreditsMessage(''), 5000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchImageCredits]);

  const handlePurchaseCredits = async (quantity: number) => {
    if (!user?.userId) return;
    
    setIsAddingCredits(true);
    setAddCreditsMessage('Redirecting to checkout...');

    try {
      const { purchaseImageCredits } = await import('../../utils/api');
      const checkoutUrl = await purchaseImageCredits(quantity, selectedBook?.FileId);
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setAddCreditsMessage('Error creating checkout session. Please try again.');
      setTimeout(() => setAddCreditsMessage(''), 5000);
    } finally {
      setIsAddingCredits(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Credits
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', md: '1rem' } }}>
          Purchase image credits to generate AI images for your books
        </Typography>
      </Box>

      {addCreditsMessage && (
        <Alert 
          severity={addCreditsMessage.includes('Error') ? 'error' : 'success'}
          sx={{ mb: 3 }}
        >
          {addCreditsMessage}
        </Alert>
      )}

      <PricingSelector
        onPurchase={handlePurchaseCredits}
        currentCredits={availableImages || 0}
        isLoading={isAddingCredits}
        recommendedQuantity={selectedBook?.RecommendedQuantity ? Math.max(0, selectedBook.RecommendedQuantity - (selectedBook.ImageCount || 0)) : 10}
        pricingTiers={selectedBook?.PricingTiers || [
          { minQuantity: 10, maxQuantity: 10, pricePerImage: 0.20, description: "10 images (minimum)" },
          { minQuantity: 11, maxQuantity: 49, pricePerImage: 0.18, description: "11-49 images" },
          { minQuantity: 50, maxQuantity: 99, pricePerImage: 0.16, description: "50-99 images" },
          { minQuantity: 100, maxQuantity: 999999, pricePerImage: 0.15, description: "100+ images" }
        ]}
        bookTitle={selectedBook?.Title || selectedBook?.FileName}
      />
    </Box>
  );
};

export default CreditsPage; 